import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import ExplorePage from './pages/ExplorePage';
import Account from './pages/Account';
import ItineraryPage from './pages/ItineraryPage';
import ItineraryViewPage from './pages/ItineraryViewPage';
import DestinationPage from './pages/DestinationPage';
import GuideEditorPage from './pages/GuideEditorPage';
import GuideDetailPage from './pages/GuideDetailPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ContactUs from './pages/ContactUs';
import AboutUs from './pages/AboutUs';
import AdminPage from './pages/Admin/AdminPage';
import FeedbackAdminPage from './pages/Admin/FeedbackAdminPage';
import PromoteUserAdminPage from './pages/Admin/PromoteUserAdminPage';
import ReportAdminPage from './pages/Admin/ReportAdminPage';
import AdminCreateBlogPage from './pages/Admin/AdminCreateBlogPage';
import GuideReviewsAdminPage from './pages/Admin/GuideReviewsAdminPage';
import AdminBlogOriginsPage from './pages/Admin/AdminBlogOriginsPage';
import { PlaceProvider } from './components/itinerary/PlaceContext';
import './styles/index.css';
import './styles/components/common/NavBar.css';
import VerifyEmail from './components/user/VerifyEmail';
import { AuthProvider, useAuth } from './AuthContext';
import { SuggestionsProvider } from './components/explore_page/SuggestionsContext';
import { QuestionnaireProvider } from './components/questionnaire/QuestionnaireContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import { MapProvider } from './components/maps/MapContext';
import { ChatBotProvider } from './components/ChatBot/ChatBotContext';
import { ItinerarySuggestionsProvider } from './components/itinerary/ItinerarySuggestionsContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import UsernameModal from './components/user/UsernameModal';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import ResponsiveLayout from './components/ResponsiveLayout';
import ScrollToTop from './components/common/ScrollToTop';
import { CookieConsentProvider } from './components/cookies/CookieConsentContext';
import AnalyticsProvider from './components/firebase_utils/AnalyticsProvider';
import CookieBanner from './components/cookies/CookieBanner';
import { PlanSelectionProvider } from './components/user/PlanSelectionContext';

const AppContent = () => {
  const location = useLocation();
  const { showUsernameModal, hideUsernameModal } = useAuth();
  const hideFooterRoutes = ['/itinerary/', '/explore/ai-agent', '/verify-email'];
  const isExplorePage = location.pathname === '/explore';
  const isItineraryRoute = /^\/itinerary\/[a-zA-Z0-9-_]+$/.test(location.pathname);
  const isGuideEditorRoute = /^\/guide\/[^/]+\/edit\/[^/]+$/i.test(location.pathname) || /^\/guide\/[^/]+$/i.test(location.pathname);
  const isGuideDetailRoute = /^\/guide\/[^/]+\/[^/]+$/i.test(location.pathname);
  const shouldHideFooter =
    isExplorePage ||
    hideFooterRoutes.some(route => location.pathname.startsWith(route)) ||
    isItineraryRoute ||
    isGuideEditorRoute ||
    isGuideDetailRoute;
  const excludeNavBarRoutes = ['/'];
  const hasNavbar = !excludeNavBarRoutes.includes(location.pathname);
  const hasFooter = !shouldHideFooter;
  const isLandingPage = location.pathname === '/';
  return (
    <ResponsiveLayout hasNavbar={hasNavbar} hasFooter={hasFooter} backgroundColor={isLandingPage ? '#f0f2f5' : '#f0f2f5'}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/explore/ai-agent"
          element={
            <ProtectedRoute requiredChecks={{ login: true, verified: true, support: false }}>
              <MapProvider>
                <PlaceProvider>
                  <ExplorePage />
                </PlaceProvider>
              </MapProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/itinerary/:origin_id/edit/:itinerary_id"
          element={
            <ProtectedRoute requiredChecks={{ login: true, verified: true, premium: true }}>
              <MapProvider>
                <PlaceProvider>
                  <ItineraryPage />
                </PlaceProvider>
              </MapProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/itinerary/:origin_id/:itinerary_id"
          element={
            <ProtectedRoute requiredChecks={{ login: true, verified: true }}>
              <MapProvider>
                <PlaceProvider>
                  <ItineraryViewPage />
                </PlaceProvider>
              </MapProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute requiredChecks={{ login: true }}>
              <Account />
            </ProtectedRoute>
          }
        />
        <Route path="/destination/:origin_id" element={<DestinationPage />} />
        <Route path="/destination/browse" element={<DestinationPage />} />
        <Route
          path="/guide/:origin_id/edit/:guide_id"
          element={
            <ProtectedRoute requiredChecks={{ login: true, verified: true }}>
              <GuideEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guide/:origin_id/:guide_id"
          element={
            <ProtectedRoute requiredChecks={{ login: false }}>
              <GuideDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guide/:origin_id"
          element={
            <ProtectedRoute requiredChecks={{ login: true, verified: true }}>
              <GuideEditorPage />
            </ProtectedRoute>
          }
        />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/about" element={<AboutUs />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredChecks={{ login: true, admin: true }}>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/feedback"
          element={
            <ProtectedRoute requiredChecks={{ login: true, admin: true }}>
              <FeedbackAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/promote-user"
          element={
            <ProtectedRoute requiredChecks={{ login: true, admin: true }}>
              <PromoteUserAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <ProtectedRoute requiredChecks={{ login: true, admin: true }}>
              <ReportAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/create-blog"
          element={
            <ProtectedRoute requiredChecks={{ login: true, admin: true }}>
              <AdminCreateBlogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/create-blog/editor/:origin_id"
          element={
            <ProtectedRoute requiredChecks={{ login: true, admin: true }}>
              <GuideEditorPage contentType="blog" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/guide-reviews"
          element={
            <ProtectedRoute requiredChecks={{ login: true, admin: true }}>
              <GuideReviewsAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/blog-origins"
          element={
            <ProtectedRoute requiredChecks={{ login: true, admin: true }}>
              <AdminBlogOriginsPage />
            </ProtectedRoute>
          }
        />
      </Routes>
      <ToastContainer />
      <UsernameModal show={showUsernameModal} handleClose={hideUsernameModal} />
    </ResponsiveLayout>
  );
};

function App() {
  return (
    <AuthProvider>
      <SuggestionsProvider>
        <QuestionnaireProvider>
          <ChatBotProvider>
            <ItinerarySuggestionsProvider>
              <HelmetProvider>
                <CookieConsentProvider>
                  <AnalyticsProvider>
                    <PlanSelectionProvider>
                      <Router>
                        <ScrollToTop />
                        <CookieBanner />
                        <AppContent />
                      </Router>
                    </PlanSelectionProvider>
                  </AnalyticsProvider>
                </CookieConsentProvider>
              </HelmetProvider>
            </ItinerarySuggestionsProvider>
          </ChatBotProvider>
        </QuestionnaireProvider>
      </SuggestionsProvider>
    </AuthProvider>
  );
}

export default App;
